import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { linkedAdjustmentsShape } from 'common/components/proptypes/commonProptypes';
import { LABEL_KEY, TOTALS_KEY } from 'common/engine/utilities/constants';
import {
  descriptionResetToBasisOfPresentation,
  setDescriptorForAdjustmentThunk,
} from 'common/engine/reducers/structuredDocumentDescriptionSlice';
import { getAreAdjustmentsToggledFromDefault } from 'common/engine/selectors/adjustmentTogglesSelectors';
import { getIsViewingToggled } from 'common/engine/reducers/calcEngine';
import {
  isProjectArchived,
  isProjectPaused,
} from 'common/helpers/projectStatusesUtils';
import {
  useProjectsStatuses,
  getProjectStatus,
} from 'common/hooks/useProjectsStatuses';
import AdjustmentGroupDetails from './AdjustmentGroupDetails';
import { getGroupLinksDescriptor } from './AdjustmentGroupHelpers';
import AdjustmentGroupHeader from './AdjustmentGroupHeader';
import AdjustmentGroupEbitdaContribution from './AdjustmentGroupEbitdaContribution';
import AdjustmentGroupWorkingCapitalContribution from './AdjustmentGroupWorkingCapitalContribution';

const AdjustmentGroup = ({
  projectId,
  documentCode,
  contentId,
  contentVersionNumber,
  group,
  groupName,
  timePeriods,
  ebitdaContributions,
  workingCapitalContributions,
  totalOverrideValue,
  linkedAdjustments,
  showToggles,
  adjustmentToggles,
  isLastGroup,
  showTotalRow,
  targetAdjustmentType,
  targetAdjustmentName,
  nameOfAdjustmentToExpand,
  isPublishMode,
  isEditMode,
  adjustmentOptions,
  targetDocumentOptions,
  selectedLinkedAdjustments,
  setFieldValue,
  showLinkedAdjustments,
  isClientView,
}) => {
  const dispatch = useDispatch();
  const renderCount = useRef(1);
  const projectsStates = useProjectsStatuses();
  const projectStatus = getProjectStatus(projectsStates, projectId);
  const [isExpanded, setIsExpanded] = useState(
    targetAdjustmentType === groupName ||
      nameOfAdjustmentToExpand === 'all' ||
      nameOfAdjustmentToExpand === groupName
  );
  const toggleExpanded = () => {
    if (isExpanded) {
      dispatch(descriptionResetToBasisOfPresentation());
    }
    setIsExpanded((isExpandedPrev) => !isExpandedPrev);
  };

  useEffect(() => {
    if (isPublishMode) {
      if (
        nameOfAdjustmentToExpand === 'all' ||
        nameOfAdjustmentToExpand === groupName
      ) {
        setIsExpanded(true);
      }
    }
  }, [nameOfAdjustmentToExpand, isPublishMode, setIsExpanded, groupName]);

  useEffect(() => {
    renderCount.current += 1;
  });

  if (
    renderCount.current === 1 &&
    targetAdjustmentType !== null &&
    targetAdjustmentName !== null
  ) {
    dispatch(
      setDescriptorForAdjustmentThunk(
        targetAdjustmentType,
        targetAdjustmentName,
        documentCode,
        projectId,
        contentId,
        contentVersionNumber,
        isClientView
      )
    );
  }

  const groupLinksDescriptor = getGroupLinksDescriptor(
    groupName,
    linkedAdjustments
  );

  const areAdjustmentsToggledFromDefault = useSelector(
    getAreAdjustmentsToggledFromDefault
  );

  const isViewingToggled = useSelector(getIsViewingToggled);

  const disableAllToggles =
    isProjectArchived(projectStatus) ||
    isProjectPaused(projectStatus) ||
    (areAdjustmentsToggledFromDefault && !isViewingToggled);

  return (
    <>
      <AdjustmentGroupHeader
        documentCode={documentCode}
        group={group}
        title={groupName}
        expanded={isExpanded}
        onClick={toggleExpanded}
        totalOverrideValue={totalOverrideValue}
        timePeriods={timePeriods}
        hasLinks={groupLinksDescriptor.hasLinks}
        hasBrokenLinks={groupLinksDescriptor.hasBrokenLinks}
        isPublishMode={isPublishMode}
      />

      {isExpanded && (
        <AdjustmentGroupDetails
          projectId={projectId}
          documentCode={documentCode}
          contentId={contentId}
          contentVersionNumber={contentVersionNumber}
          group={group}
          groupName={groupName}
          showTotalRow={showTotalRow}
          totalOverrideValue={totalOverrideValue}
          timePeriods={timePeriods}
          showToggles={showToggles}
          adjustmentToggles={adjustmentToggles}
          disableAllToggles={disableAllToggles}
          linkedAdjustments={linkedAdjustments}
          isPublishMode={isPublishMode}
          isEditMode={isEditMode}
          adjustmentOptions={adjustmentOptions}
          targetDocumentOptions={targetDocumentOptions}
          selectedLinkedAdjustments={selectedLinkedAdjustments}
          setFieldValue={setFieldValue}
          showLinkedAdjustments={showLinkedAdjustments}
          isClientView={isClientView}
        />
      )}

      {ebitdaContributions[groupName] && (
        <AdjustmentGroupEbitdaContribution
          timePeriods={timePeriods}
          rowDescription={ebitdaContributions[groupName][LABEL_KEY]}
          totals={ebitdaContributions[groupName][TOTALS_KEY]}
          isTotalsRowStyle={isLastGroup}
          documentCode={documentCode}
          isPublishMode={isPublishMode}
        />
      )}

      {workingCapitalContributions[groupName] && (
        <AdjustmentGroupWorkingCapitalContribution
          timePeriods={timePeriods}
          rowDescription={workingCapitalContributions[groupName][LABEL_KEY]}
          totals={workingCapitalContributions[groupName][TOTALS_KEY]}
          isTotalsRowStyle={isLastGroup}
          documentCode={documentCode}
          isPublishMode={isPublishMode}
        />
      )}
    </>
  );
};

AdjustmentGroup.propTypes = {
  projectId: PropTypes.string.isRequired,
  documentCode: PropTypes.string.isRequired,
  contentId: PropTypes.number.isRequired,
  contentVersionNumber: PropTypes.number.isRequired,
  group: PropTypes.object.isRequired,
  groupName: PropTypes.string.isRequired,
  timePeriods: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  ebitdaContributions: PropTypes.object,
  workingCapitalContributions: PropTypes.object,
  totalOverrideValue: PropTypes.string,
  linkedAdjustments: PropTypes.arrayOf(linkedAdjustmentsShape),
  showToggles: PropTypes.bool,
  adjustmentToggles: PropTypes.object,
  isLastGroup: PropTypes.bool,
  showTotalRow: PropTypes.bool,
  isPublishMode: PropTypes.bool,
  targetAdjustmentType: PropTypes.string,
  targetAdjustmentName: PropTypes.string,
  nameOfAdjustmentToExpand: PropTypes.string,
};

AdjustmentGroup.defaultProps = {
  ebitdaContributions: {},
  workingCapitalContributions: {},
  totalOverrideValue: undefined,
  linkedAdjustments: [],
  showToggles: true,
  adjustmentToggles: {},
  isLastGroup: false,
  showTotalRow: true,
  isPublishMode: false,
  targetAdjustmentType: null,
  targetAdjustmentName: null,
  nameOfAdjustmentToExpand: null,
};

export default AdjustmentGroup;
